/**
 * A custom domain's DNS records as a BIND zone file, so an owner can import
 * them in one step instead of copying three values into three forms.
 *
 * BIND (RFC 1035 master file format) is the one format DNS providers agree
 * on: Cloudflare, GoDaddy, Route 53 and many others import it. Three
 * choices keep one file valid at all of them:
 *
 * 1. Every name is ABSOLUTE, with a trailing dot, and there is no `$ORIGIN`.
 *    The file cannot know which zone the owner will import it into (the
 *    registrable domain needs the Public Suffix List, see dns-name.ts), and a
 *    relative name imported into the wrong zone becomes
 *    `app.customer.com.customer.com`. An absolute name means the same thing
 *    everywhere.
 *
 * 2. The CNAME carries `; cf_tags=cf-proxied:false`. Cloudflare reads that
 *    comment on import and it overrides the import dialog's "Proxy imported DNS
 *    records" box, so the record lands DNS only (the grey cloud) whatever the
 *    owner ticks. A proxied record is the one setting that silently breaks a
 *    custom domain. Every other provider sees an ordinary comment.
 *
 * 3. TTL is 3600. Some providers refuse anything under 600, and nothing here
 *    changes often enough for a shorter one to matter.
 *
 * Nothing here knows about React or writes copy for the screen. The comments
 * inside the file stay in English, like the record syntax around them.
 */

import type { CustomDomain } from './real-api';

const TTL = 3600;

/** RFC 1035 caps one character-string at 255 bytes; longer TXT data is split. */
const TXT_CHUNK = 255;

/** `app.customer.com` to `app.customer.com.`, whether or not it had the dot. */
function absolute(name: string): string {
  return `${name.trim().replace(/\.+$/, '')}.`;
}

/** One TXT value as quoted character-strings, escaping `\` and `"`. */
function txtData(value: string): string {
  const chunks: string[] = [];
  for (let i = 0; i < value.length; i += TXT_CHUNK) {
    chunks.push(value.slice(i, i + TXT_CHUNK));
  }
  if (chunks.length === 0) chunks.push('');
  return chunks.map((chunk) => `"${chunk.replace(/[\\"]/g, (c) => `\\${c}`)}"`).join(' ');
}

/**
 * True when the file would carry every record the owner needs. Before
 * Cloudflare mints the certificate token the file would be missing a record,
 * and a file that imports cleanly but leaves the certificate stuck is worse
 * than no file.
 */
export function zoneFileReady(domain: CustomDomain): boolean {
  return domain.cname.value.trim() !== '' && domain.txtRecords.length > 0;
}

export function toZoneFile(domain: CustomDomain, now: Date = new Date()): string {
  const lines = [
    `; DNS records for ${domain.hostname} on SV Cloud`,
    `; Generated ${now.toISOString().slice(0, 10)}. BIND zone file format.`,
    ';',
    '; Cloudflare: DNS > Records > Import and Export > Import DNS records.',
    ';   The CNAME is imported as DNS only (grey cloud) because of the cf_tags note.',
    '; Other providers: look for "Import zone file". If there is none, add the',
    ';   records below by hand.',
    '',
    `${absolute(domain.cname.name)}\t${TTL}\tIN\tCNAME\t${absolute(domain.cname.value)} ; cf_tags=cf-proxied:false`,
    ...domain.txtRecords.map(
      (record) => `${absolute(record.name)}\t${TTL}\tIN\tTXT\t${txtData(record.value)}`,
    ),
  ];
  return `${lines.join('\n')}\n`;
}

/** `app.customer.com-dns.txt`. Plain text, so a double click opens it in an editor. */
export function zoneFileName(domain: CustomDomain): string {
  return `${domain.hostname.replace(/\.+$/, '')}-dns.txt`;
}
